import { useMemo, useState } from 'react';

import { sortTransfersByBudgetImpact } from '@lib/my-team/utils/sort-transfers';
import type {
  PlannerBenchPlayer,
  PlannerSquadPlayer,
  TransferEvent
} from '@api-service/planner';
import type { Player } from '@api-service/players';

export type OutgoingPlayer = PlannerSquadPlayer | PlannerBenchPlayer;

export type StagedTransfer = {
  slot: number;
  outgoing: OutgoingPlayer;
  incoming: Player | null;
};

export type UseTransferModeResult = {
  stagedTransfers: StagedTransfer[];
  pendingIncomingForSlot: number | null;
  pendingPositions: OutgoingPlayer['position'][];
  projectedBank: number;
  canCommit: boolean;
  transferOut: (slot: number, outgoing: OutgoingPlayer) => void;
  transferIn: (incoming: Player) => void;
  cancelTransferOut: (slot: number) => void;
  reset: () => void;
  buildTransferEvents: (gameweek: number) => TransferEvent[];
};

const findFirstPendingSlot = (transfers: StagedTransfer[]) =>
  transfers.find((transfer) => transfer.incoming === null)?.slot ?? null;

const POSITION_DISPLAY_ORDER: OutgoingPlayer['position'][] = [
  'GK',
  'DEF',
  'MID',
  'FWD'
];

/**
 * Holds the user's pending transfer set client-side until they commit. Mirrors
 * scoreline-app's useTransferMode shape so callers can keep similar UI flows:
 * pick a starter to transfer out, then pick a replacement from the player
 * list. The full set is dispatched as a batched POST when the user commits.
 */
export const useTransferMode = (currentBank: number): UseTransferModeResult => {
  const [stagedTransfers, setStagedTransfers] = useState<StagedTransfer[]>([]);

  const pendingIncomingForSlot = findFirstPendingSlot(stagedTransfers);
  // Every position still awaiting a replacement, not just the first staged
  // one: transfers staged across positions must all stay pickable at once
  // (FFH-2088). Ordered GK/DEF/MID/FWD for display.
  const pendingPositions = useMemo(() => {
    const awaiting = new Set(
      stagedTransfers
        .filter((transfer) => transfer.incoming === null)
        .map((transfer) => transfer.outgoing.position)
    );
    return POSITION_DISPLAY_ORDER.filter((position) => awaiting.has(position));
  }, [stagedTransfers]);

  const projectedBank = useMemo(
    () =>
      stagedTransfers.reduce((bank, transfer) => {
        const out = transfer.outgoing.sellingPrice;
        if (!transfer.incoming) return bank + out;
        return bank + out - transfer.incoming.price;
      }, currentBank),
    [stagedTransfers, currentBank]
  );

  const canCommit =
    stagedTransfers.length > 0 &&
    stagedTransfers.every((transfer) => transfer.incoming !== null) &&
    projectedBank >= 0;

  const transferOut = (slot: number, outgoing: OutgoingPlayer) => {
    setStagedTransfers((previous) => {
      if (previous.some((transfer) => transfer.slot === slot)) return previous;
      return [...previous, { slot, outgoing, incoming: null }];
    });
  };

  const transferIn = (incoming: Player) => {
    setStagedTransfers((previous) => {
      // Route the pick to a slot awaiting a player of the same position, not
      // simply the first pending slot: with transfers staged across positions
      // the user can fill them in any order, and a MID must never land in a
      // GK slot.
      const target = previous.find(
        (transfer) =>
          transfer.incoming === null &&
          transfer.outgoing.position === incoming.position
      );
      if (!target) return previous;
      return previous.map((transfer) =>
        transfer.slot === target.slot ? { ...transfer, incoming } : transfer
      );
    });
  };

  const cancelTransferOut = (slot: number) => {
    setStagedTransfers((previous) =>
      previous.filter((transfer) => transfer.slot !== slot)
    );
  };

  const reset = () => setStagedTransfers([]);

  const buildTransferEvents = (gameweek: number): TransferEvent[] => {
    const complete = stagedTransfers.filter(
      (transfer): transfer is StagedTransfer & { incoming: Player } =>
        transfer.incoming !== null
    );
    const withPrices = complete.map((transfer) => ({
      ...transfer,
      outgoingPrice: transfer.outgoing.sellingPrice,
      incomingPrice: transfer.incoming.price
    }));
    return sortTransfersByBudgetImpact(withPrices).map((transfer) => ({
      type: 'TRANSFER',
      gameweek,
      outgoingPlayerId: transfer.outgoing.id,
      incomingPlayerId: transfer.incoming.id
    }));
  };

  return {
    stagedTransfers,
    pendingIncomingForSlot,
    pendingPositions,
    projectedBank,
    canCommit,
    transferOut,
    transferIn,
    cancelTransferOut,
    reset,
    buildTransferEvents
  };
};
